// ImagePreview (E7.2.1) — thumbnail inline + lightbox com zoom.
//
// Híbrido lazy (decisão congelada): imagem renderiza DIRETO de `content.url`
// (sem chamar o dispatcher). Clique abre `yet-another-react-lightbox` + Zoom.
//
// A `url` é uma signed-URL HMAC re-assinada a cada render do pai (novo
// `?expires&sig`) — o chat/atendimentos faz poll e re-renderiza. Se o slide
// seguisse a `url` viva, um poll DURANTE a visualização trocaria a `src` do
// lightbox aberto → o browser RE-BAIXA a imagem e o zoom RESETA (o bug do
// "zoom recarregando": não é o gesto de zoom, é o dwell no lightbox cruzando
// um poll). Solução: `displayUrl` congelada POR CAMINHO (o pathname
// `/storage/file/<storage_key>` é fixo por imagem; só o query faz churn):
//  - re-render por poll (mesmo pathname) NÃO troca a src → sem re-download;
//  - ABRIR o lightbox re-captura a assinatura FRESCA (evita abrir com uma url
//    congelada já expirada); a partir daí o freeze segura o zoom;
//  - `onError` no thumbnail tenta UMA re-assinatura fresca (auto-heal de expiry)
//    por pathname — se a fresca também falhar, desiste (não re-baixa a cada poll).
//
// `baixavel` põe o "Baixar imagem" na barra (plugin Download), com a NOSSA função
// de baixar: a padrão do plugin faz um XHR SÍNCRONO quando acha que a url é de
// outra origem — e a `data:` do b64 legado do portal cai nesse caso.
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import Download from "yet-another-react-lightbox/plugins/download";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

import { triggerBrowserDownload } from "@/lib/browser-download";

export interface ImagePreviewProps {
  url: string;
  filename?: string;
  /**
   * Classe do thumbnail. O default (`max-h-64`, 256px) serve o chat, onde a
   * imagem é anexo numa conversa. Telas que exibem DOCUMENTO para leitura — o
   * comprovante do Lotese, onde o operador compara o valor do papel — precisam
   * de mais altura; encolher ali seria regressão, não padronização.
   */
  thumbClassName?: string;
  /**
   * Marcação sobre o thumbnail: onde o OCR leu o valor (C). Só no thumbnail —
   * o lightbox é componente de terceiro e injetar overlay ali exigiria brigar
   * com o ciclo de vida do slide. A marcação serve para LOCALIZAR; o zoom, que
   * já existe, serve para LER.
   */
  overlay?: ReactNode;
  /**
   * Botão "Baixar imagem" na barra do visualizador. Opt-in: as conversas (chat
   * interno, Central, portal) ligam; o comprovante do Lotese fica como estava.
   */
  baixavel?: boolean;
}

const THUMB_PADRAO = "max-h-64 max-w-full object-contain";

const PLUGINS_SO_ZOOM = [Zoom];
const PLUGINS_COM_DOWNLOAD = [Zoom, Download];

// Os títulos dos botões (title + aria-label) em português — inclusive as setas,
// que a biblioteca desenha mesmo com uma imagem só — e o que o leitor de tela
// anuncia: o diálogo, a galeria e a posição da imagem ("1 de 1").
const ROTULOS = {
  Previous: "Anterior",
  Next: "Próxima",
  Close: "Fechar",
  "Zoom in": "Ampliar",
  "Zoom out": "Reduzir",
  Download: "Baixar imagem",
  Lightbox: "Visualizador de imagem",
  "Photo gallery": "Galeria de imagens",
  Carousel: "Carrossel",
  Slide: "Imagem",
  "{index} of {total}": "{index} de {total}",
};

// Chave estável da url = origin+pathname (ignora ?query = expires/sig). Para
// `data:` URI (b64 legado) a própria string é a identidade — curto-circuito
// evita alocar o base64 inteiro num `new URL` a cada render. `catch` cobre url
// relativa/malformada.
function stableKey(u: string): string {
  if (u.startsWith("data:")) return u;
  try {
    const p = new URL(u, window.location.origin);
    return p.origin + p.pathname;
  } catch {
    return u;
  }
}

export function ImagePreview({
  url,
  filename,
  thumbClassName = THUMB_PADRAO,
  overlay,
  baixavel = false,
}: ImagePreviewProps) {
  const [open, setOpen] = useState(false);
  // URL exibida, estável por caminho: uma re-assinatura (mesmo pathname, novo
  // ?expires&sig) NÃO troca a src → sem re-download no thumbnail nem no slide.
  const [displayUrl, setDisplayUrl] = useState(url);
  // Pathname cuja re-assinatura fresca já tentamos após um erro — evita re-baixar
  // uma imagem persistentemente indisponível a cada poll. Reseta no onLoad.
  const triedFreshRef = useRef<string | null>(null);

  useEffect(() => {
    // Adota quando a imagem muda (pathname novo); ignora o churn de ?expires&sig.
    if (stableKey(url) !== stableKey(displayUrl)) setDisplayUrl(url);
  }, [url, displayUrl]);

  // `slides` memoizado na displayUrl: um novo array/objeto a cada render também
  // faria o lightbox recarregar o slide aberto.
  const slides = useMemo(() => [{ src: displayUrl, alt: filename }], [displayUrl, filename]);

  const openLightbox = () => {
    // Re-captura a assinatura FRESCA no momento de abrir → o lightbox nunca abre
    // com uma url congelada/expirada; daqui em diante o freeze segura o zoom.
    setDisplayUrl(url);
    setOpen(true);
  };

  const healOnError = () => {
    // A url congelada pode ter expirado (TTL) → UMA tentativa de adotar a
    // assinatura fresca (mesmo pathname). Se a fresca também falhar, desiste
    // (não re-baixa em loop uma imagem indisponível a cada poll).
    const key = stableKey(displayUrl);
    if (triedFreshRef.current === key) return;
    triedFreshRef.current = key;
    if (displayUrl !== url) setDisplayUrl(url);
  };

  return (
    <>
      <button
        type="button"
        onClick={openLightbox}
        // `relative` ancora o overlay: as caixas são posicionadas em
        // porcentagem DESTE container, que embrulha exatamente a imagem.
        className="relative block overflow-hidden rounded border bg-muted/30"
        data-testid="image-preview-thumb"
        aria-label={`Ampliar ${filename || "imagem"}`}
      >
        <img
          src={displayUrl}
          alt={filename || "imagem"}
          loading="lazy"
          className={thumbClassName}
          onLoad={() => {
            triedFreshRef.current = null;
          }}
          onError={healOnError}
        />
        {overlay}
      </button>
      <Lightbox
        open={open}
        close={() => setOpen(false)}
        slides={slides}
        plugins={baixavel ? PLUGINS_COM_DOWNLOAD : PLUGINS_SO_ZOOM}
        labels={ROTULOS}
        zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }}
        // Baixa a imagem que está na tela (a `displayUrl` do slide; a assinatura
        // vale 7+ dias, não vence com o visualizador aberto).
        download={{
          download: ({ slide }) => triggerBrowserDownload(slide.src, filename || "imagem"),
        }}
        // Radix Dialog põe `pointer-events: none` no <body> enquanto está
        // aberto e devolve `auto` só ao próprio conteúdo. O lightbox monta em
        // portal IRMÃO, fora do dialog — herda o `none` e os botões de zoom
        // aparecem sem receber clique nenhum. No chat isso não acontece (não há
        // dialog em volta); nas telas do Lotese o comprovante abre DENTRO de um
        // Dialog, e sem esta linha o zoom fica decorativo.
        styles={{ root: { pointerEvents: "auto" } }}
      />
    </>
  );
}

export default ImagePreview;
